import { configuredTransportOrigin } from '../../../../shared/config/api-origin';
import { ASSET_LIMIT, ASSET_SCOPE } from '../consumer/asset-contract';
import type { LocalAssetReadPort } from './local-asset-port';
import {
  AssetWireError,
  parseLocalAssetCatalogWire,
  parseLocalAssetDetailWire,
} from './local-asset-wire';
export function createLiveLocalAssetAdapter({
  baseOrigin,
  fetchImpl,
}: {
  readonly baseOrigin: string;
  readonly fetchImpl?: typeof fetch;
}): LocalAssetReadPort {
  const configured = configuredTransportOrigin(baseOrigin);
  if (!configured && !/^https?:\/\/[^/?#\\\s@]+\/?$/i.test(baseOrigin))
    throw new Error('Root loopback origin required');
  const url = new URL(configured ?? baseOrigin);
  const parts = url.hostname.split('.');
  const loopback =
    url.hostname === 'localhost' ||
    url.hostname === '[::1]' ||
    (parts.length === 4 &&
      parts[0] === '127' &&
      parts.every((part) => /^\d+$/.test(part) && Number(part) <= 255));
  if (
    !configured &&
    (!loopback || url.username || url.password || url.search || url.hash || url.pathname !== '/')
  )
    throw new Error('Root loopback origin required');
  async function get(path: string, signal: AbortSignal): Promise<unknown> {
    const response = await (fetchImpl ?? globalThis.fetch)(url.origin + path, {
      method: 'GET',
      credentials: 'omit',
      redirect: 'error',
      cache: 'no-store',
      signal,
    });
    if (response.status !== 200) throw new Error('Local asset HTTP request failed');
    if (
      response.headers.get('Content-Type')?.split(';')[0]?.trim().toLowerCase() !==
      'application/json'
    )
      throw new AssetWireError();
    try {
      return await response.json();
    } catch (error) {
      if (error instanceof SyntaxError) throw new AssetWireError();
      throw error;
    }
  }
  return {
    async readCatalog(query, signal) {
      if (
        query.scope !== ASSET_SCOPE ||
        query.limit !== ASSET_LIMIT ||
        Object.keys(query).length !== 2
      )
        throw new Error('Fixed catalog query required');
      return parseLocalAssetCatalogWire(await get('/read-models/local-asset-catalog', signal));
    },
    async readDetail(assetId, signal) {
      if (typeof assetId !== 'string' || assetId.trim() === '' || ['.', '..'].includes(assetId))
        throw new Error('Exact routable asset ID required');
      return parseLocalAssetDetailWire(
        await get('/read-models/local-assets/' + encodeURIComponent(assetId), signal),
        assetId,
      );
    },
  };
}
